// src/hooks/useIoApi.tsx

import { useCallback } from 'react'

import { authHeader, notifyError } from '../api/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

export const useIoApi = () => {
  const withRequest = useCallback(async fn => {
    try {
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          notifyError(`IoApi: ${e2?.message}`)
          return null
        }
      }

      notifyError(`IoApi: ${error?.message}`)
      return null
    }
  }, [])

  const exportXls = useCallback(
    (
      operator,
      reporting_date,
      folders,
      granularity,
      recipients,
      user_locales,
      cols
    ) =>
      withRequest(async () => {
        try {
          const url = `${REACT_APP_API_URL}/iodata/admin/iodata/`
          const options = {
            method: 'POST',
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              folders,
              granularity,
              operator,
              recipients: {
                to: String(recipients.to)
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean),
                cc: String(recipients.cc)
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean),
                bcc: String(recipients.bcc)
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean),
              },
              reporting_date,
              user_locales,
              cols,
            }),
          }

          const res = await fetch(url, options)

          if (res.status === 202) {
            const data = await res.json().catch(() => null)
            const jobId = data?.jobId ?? null

            return { jobId }
          }

          const text = await res.text().catch(() => '')
          throw {
            message: text || `HTTP ${res.status}`,
            status: res.status,
          }
        } catch (error) {
          notifyError(error.message)
          return { jobId: null }
        }
      }),
    [withRequest]
  )

  const getIoJobStatus = useCallback(
    jobId =>
      withRequest(async () => {
        try {
          const url = `${REACT_APP_API_URL}/iodata/admin/iodata/status?jobId=${encodeURIComponent(
            jobId
          )}`
          const res = await fetch(url, { headers: authHeader() })

          if (!res.ok) {
            const text = await res.text().catch(() => '')
            throw {
              message: text || `HTTP ${res.status}`,
              status: res.status,
            }
          }

          const data = await res.json()
          return data // { status: 'PENDING'|'RUNNING'|'DONE'|'FAILED', ... }
        } catch (error) {
          notifyError(error.message)
          throw error
        }
      }),
    [withRequest]
  )

  return {
    exportXls,
    getIoJobStatus,
  }
}
